import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTokens } from "../hooks/useTokens";
import TokenPurchaseModal from "./TokenPurchaseModal";
import MockInterviewModal from "./MockInterviewModal";
import useCVAnalysis from "../hooks/useCVAnalysis";
import { supabase } from "../supabase";



// ToolTip component
const ToolTip = ({ toolTipLabel }) => {
    return (
        <div className="absolute left-full ml-3 top-1/2 -translate-y-1/2 z-10 opacity-0 group-hover:opacity-100 bg-[#1E1E1E]/80 text-white text-sm py-1 px-3 rounded-md whitespace-nowrap shadow-lg transition-all duration-200">
            {toolTipLabel}
            {/* Arrow */}
            <div className="absolute left-[-5px] top-1/2 -translate-y-1/2 w-0 h-0 border-t-4 border-b-4 border-r-4 border-t-transparent border-b-transparent border-r-[#1E1E1E]/80"></div>
        </div>
    );
};

// NavItem component
const NavItem = ({
    icon,
    label,
    tooltip,
    onClick,
    isSidebarOpen,
    comingSoon,
    active = false,
}) => {
    return (
        <li
            className={`relative group flex items-center py-2 rounded-lg cursor-pointer text-white
            ${comingSoon ? "" : ""}
            ${label ? "gap-4 px-3" : "justify-center"}
            `}
            onClick={onClick}
            aria-disabled={comingSoon}
            style={comingSoon ? { cursor: "not-allowed", opacity: 0.5 } : {}}
        >
            <div className="w-5 h-5 flex items-center justify-center">
                {icon}
            </div>
            {label && <span className="text-white leading-5 font-normal whitespace-nowrap">{label}</span>}

            {/* Tooltip */}
            {!isSidebarOpen && tooltip && <ToolTip toolTipLabel={tooltip} />}

            {/* Vertical line indicator */}
            <span
                className={`
                absolute right-0 h-6 w-[4px] bg-white
                transition-opacity
                ${active ? "opacity-100" : "opacity-0 group-hover:opacity-100"}
            `}
            />
        </li>
    );
};

// Divider component
const Divider = () => (
    <hr className="border-b border-b-gray-600 opacity-20 w-full" />
);

const Sidebar = ({ isOpen, onToggle, isMobile }) => {
    const { user, signOut } = useAuth();
    const { tokens, loading: tokensLoading, fetchTokens } = useTokens();
    const navigate = useNavigate();
    const location = useLocation();
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [showMockInterviewModal, setShowMockInterviewModal] = useState(false);
    const [mockInterviewPaid, setMockInterviewPaid] = useState(false);
    const [mockInterviewConsented, setMockInterviewConsented] = useState(false);
    const { getLatestAnalysis } = useCVAnalysis(user);

    // Check if user has paid and consented for mock interview
    useEffect(() => {
        const checkMockInterviewStatus = async () => {
            if (!user?.id) return;

            try {
                const { data, error } = await supabase
                    .from('user_tokens')
                    .select('mock_interview_paid, mock_interview_consent')
                    .eq('user_id', user.id)
                    .single();

                if (!error && data) {
                    setMockInterviewPaid(data.mock_interview_paid || false);
                    setMockInterviewConsented(data.mock_interview_consent || false);
                } else {
                    setMockInterviewPaid(false);
                    setMockInterviewConsented(false);
                }
            } catch (err) {
                console.error("Error checking mock interview status:", err);
                setMockInterviewPaid(false);
                setMockInterviewConsented(false);
            }
        };

        checkMockInterviewStatus();
    }, [user]);

    const handleCVAnalyzerClick = async (e) => {
        e.preventDefault();
        if (user) {
            const latestAnalysis = await getLatestAnalysis();
            if (latestAnalysis && latestAnalysis.cv_analysis_results) {
                navigate(`/cv-analysis-results?id=${latestAnalysis.id}`);
            } else {
                navigate("/cv-analyser");
            }
        } else {
            navigate("/cv-analyser");
        }
        
        // Close sidebar on mobile after navigation
        if (isMobile) {
            onToggle();
        }
    };

    const handleTokenPurchaseSuccess = () => {
        fetchTokens();
        setShowTokenModal(false);
    };

    if (!user) {
        return null;
    }

    return (
        <nav
            className={`flex flex-col text-white ${
                isMobile ? "w-[270px]" : (!isOpen ? "w-[78px]" : "w-[270px]")
            } h-screen`}
            style={{ backgroundColor: '#1e1e1e' }}
        >
            {/* Logo section */}
            <header
                className={`flex items-center ${
                    (isMobile || isOpen) ? "gap-5 px-6 py-5" : "justify-center py-5"
                }`}
                style={{ backgroundColor: '#1e1e1e' }}
            >
                {(isMobile || isOpen) && (
                    <div className="flex items-center gap-3">
                        <img
                            alt="Doctors' Jobs Logo"
                            src="/DJ logo white.svg"
                            className="flex-shrink-0 h-8 w-auto object-contain cursor-pointer"
                            onClick={() => {
                                navigate("/");
                                if (isMobile) {
                                    onToggle();
                                }
                            }}
                        />
                    </div>
                )}
                {!isMobile && !isOpen && (
                    <img
                        alt="Open Sidebar"
                        src="/minimise-close.svg"
                        className="w-6 h-6 object-contain cursor-pointer transform scale-x-[-1]"
                        onClick={onToggle}
                    />
                )}
                {(isMobile || isOpen) && (
                    <div
                        className={`transition-transform duration-300`}
                    >
                        <img
                            onClick={onToggle}
                            alt="Minimise Close"
                            src="/minimise-close.svg"
                            className="w-6 h-6 object-contain cursor-pointer"
                        />
                    </div>
                )}
            </header>

            {/* Divider underneath header */}
            <div className="border-t border-gray-600"></div>

            {/* Main menu */}
            <div className={`flex-grow ${(!isMobile && !isOpen) ? "" : "overflow-y-auto"}`}>
                <ul
                    className={`flex flex-col gap-1 px-4 mt-5 ${
                        (!isMobile && !isOpen) ? "px-2" : ""
                    }`}
                >
                    <NavItem
                        icon={
                            <img
                                alt="Job Board"
                                src="/Job board icon@4x.webp"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Job Board" : ""}
                        tooltip="Job Board"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            navigate("/");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/"}
                    />

                    <NavItem
                        icon={
                            <img
                                alt="Job Applications Tracker"
                                src="/track jobs icon.svg"
                                className="object-contain"
                                style={{ 
                                    width: '40px', 
                                    height: '40px',
                                    minWidth: '40px',
                                    minHeight: '40px'
                                }}
                            />
                        }
                        label={(isMobile || isOpen) ? "Job Applications Tracker" : ""}
                        tooltip="Job Applications Tracker"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={() => {
                            navigate("/job-applications-tracker");
                            if (isMobile) {
                                onToggle();
                            }
                        }}
                        active={location.pathname === "/job-applications-tracker"}
                    />

                    <NavItem
                        icon={
                            <img
                                alt="CV Analyser"
                                src="/CV analyses@4x.webp"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "CV Analyser" : ""}
                        tooltip="CV Analyser"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={handleCVAnalyzerClick}
                        active={location.pathname === "/cv-analyser" || location.pathname === "/cv-analysis-results"}
                    />

                    <NavItem
                        icon={
                            <img
                                alt="Mock Interview"
                                src="/interview.svg"
                                className="w-5 h-5"
                            />
                        }
                        label={(isMobile || isOpen) ? "Mock Interview" : ""}
                        tooltip="Mock Interview"
                        isSidebarOpen={isMobile || isOpen}
                        onClick={async () => {
                            // Check if user has paid AND consented
                            if (mockInterviewPaid && mockInterviewConsented) {
                                // For paid and consented users, navigate and close sidebar on mobile
                                if (isMobile) {
                                    onToggle();
                                }
                                navigate("/practice-interview-questions");
                            } else {
                                // IMPORTANT: Do NOT close the mobile sidebar here.
                                // Closing it unmounts Sidebar and the modal never renders.
                                setShowMockInterviewModal(true);
                            }
                        }}
                        active={location.pathname === "/practice-interview-questions"}
                    />
                </ul>

                {/* Divider line */}
                <div className="px-4 my-4">
                    <div className="border-t border-gray-600"></div>
                </div>

                {/* Support Section */}
                <ul
                    className={`flex flex-col gap-1 px-4 ${
                        (!isMobile && !isOpen) ? "px-2" : ""
                    }`}
                >
                    {/* <NavItem
                        icon={
                            <img
                                alt="Report Issues"
                                src="/Report issue.svg"
                                className="w-5 h-5"
                            />
                        }
                        label={isOpen ? "Report Issues" : ""}
                        tooltip="Report Issues"
                        isSidebarOpen={isOpen}
                        onClick={() => navigate("/report-issues")}
                        active={location.pathname === "/report-issues"}
                    /> */}

                    {/* <NavItem
                        icon={
                            <img
                                alt="Support"
                                src="/message-question-1.svg"
                                className="w-5 h-5"
                                style={{ filter: 'brightness(0) invert(1)' }}
                            />
                        }
                        label={isOpen ? "Support" : ""}
                        tooltip="Support"
                        isSidebarOpen={isOpen}
                        onClick={() => window.open("https://tally.so/r/wb6krg", "_blank")}
                        active={false}
                    /> */}
                </ul>

            </div>

            {/* Footer */}
            <footer className="mt-auto px-4 py-4 flex flex-col gap-3">
                {/* Divider line above Suggest Ideas */}
                <div className="border-t border-gray-600"></div>

                {/* Suggest Ideas */}
                {/* {isOpen && (
                    <div className="flex bg-black rounded-lg border border-gray-600 p-4 items-center justify-between cursor-pointer" onClick={() => navigate("/suggest-ideas")}>
                        <div className="flex items-center gap-3">
                            <img
                                alt="Suggest Ideas"
                                src="/Add ideas@4x.webp"
                                className="w-5 h-5"
                            />
                            <span className="font-normal text-white">Suggest ideas</span>
                        </div>
                        <span className="text-white text-lg font-bold">+</span>
                    </div>
                )}
                
                {!isOpen && (
                    <NavItem
                        icon={
                            <img
                                alt="Suggest Ideas"
                                src="/Add ideas@4x.webp"
                                className="w-5 h-5"
                            />
                        }
                        label=""
                        tooltip="Suggest Ideas"
                        isSidebarOpen={isOpen}
                        onClick={() => navigate("/suggest-ideas")}
                    />
                )} */}

                                {/* Request Feature Button */}
                                {(isMobile || isOpen) && (
                    <div className="mb-3">
                        <button 
                            className="w-full bg-[#ececec] hover:bg-white font-bold border border-[rgba(3,56,39,0.4)] text-[#061B16] rounded-xl px-4 py-2"
                            onClick={() => window.open("https://doctors-jobs.features.vote/board", "_blank")}
                        >
                            Request a feature
                        </button>
                    </div>
                )}


                {/* tokens display */}
                {/* desktop */}
                {(isMobile || isOpen) && (
                    <div className="flex bg-white rounded-lg border border-gray-300 p-4 flex-col gap-2">
                        <div className="flex items-center gap-2">
                            <span className="font-normal text-black">
                                CV Tokens
                            </span>
                            <div className="relative group">
                                <img
                                    alt="Info Question Mark"
                                    src="/infoQuestionmark.png"
                                    className="!w-[17px] !h-[17px]"
                                />
                                {/* Tooltip */}
                                <div className="absolute left-1/2 transform -translate-x-1/2 -top-25 hidden group-hover:block w-max px-4 py-4 text-sm text-[#1e1e1e] bg-gray-100 rounded-lg shadow-lg z-10">
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Sign-Up Bonus: 250 tokens</li>
                                        <li>CV Analysis: 250 tokens</li>
                                    </ul>
                                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-gray-100 rotate-45"></div>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-end space-x-2">
                            <span className="text-3xl font-medium text-black">
                                {tokens ?? 0}
                            </span>
                            <span className="text-sm text-gray-600">
                                tokens available
                            </span>
                        </div>
                        <button
                            className="bg-black text-white rounded-full py-2 font-medium hover:opacity-90"
                            onClick={() => setShowTokenModal(true)}
                        >
                           Buy CV Scan Tokens   
                        </button>
                    </div>
                )}

                {/* mobile */}
                {!isMobile && !isOpen && (
                    <div className="relative group w-8 h-8 flex rounded items-center justify-center ml-2">
                        <svg
                            className="w-8 h-8 transform -rotate-90"
                            viewBox="0 0 100 100"
                        >
                            <circle
                                cx="50"
                                cy="50"
                                r="35"
                                stroke="#4a5568"
                                strokeWidth="8"
                                fill="transparent"
                            />
                            <circle
                                cx="50"
                                cy="50"
                                r="35"
                                stroke="#ffffff"
                                strokeWidth="8"
                                fill="transparent"
                                strokeDasharray={`${
                                    (400 * (tokens ?? 0)) / 100
                                } 219.9`}
                                strokeLinecap="round"
                            />
                        </svg>

                        {/* Add button inside the circle */}
                        <div
                            className="absolute inset-0 flex items-center justify-center cursor-pointer"
                            onClick={() => setShowTokenModal(true)}
                        >
                            <span className="text-sm font-bold text-white flex items-start justify-start mb-0.5">
                                +
                            </span>
                        </div>

                        {/* Tooltip */}
                        {!isOpen && (
                            <ToolTip toolTipLabel="Buy Tokens" />
                        )}
                    </div>
                )}

                {/* User Profile Section */}
                {(isMobile || isOpen) && user && (
                    <div className="relative">
                        <div 
                            className="flex items-center gap-3 p-3 rounded-lg"
                            style={{ 
                                backgroundColor: '#3b4040',
                                border: '1px solid #6f6f72'
                            }}
                        >
                            {/* User Avatar */}
                            <div className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                                <span className="text-white text-sm font-medium">
                                    {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                                </span>
                            </div>
                            
                            {/* User Info */}
                            <div className="flex-1 min-w-0">
                                <div className="text-white font-bold text-sm truncate">
                                    {user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}
                                </div>
                                <div className="text-white text-xs truncate">
                                    {user.email}
                                </div>
                            </div>
                            
                        </div>
                    </div>
                )}
                
                {/* Collapsed User Profile */}
                {!isMobile && !isOpen && user && (
                    <div className="relative group flex justify-center">
                        <div 
                            className="w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center"
                        >
                            <span className="text-white text-sm font-medium">
                                {user.email ? user.email.charAt(0).toUpperCase() : 'U'}
                            </span>
                        </div>
                        
                        {/* Tooltip */}
                        <ToolTip toolTipLabel={`${user.user_metadata?.full_name || user.email?.split('@')[0] || 'User'}`} />
                    </div>
                )}
            </footer>

            {/* Token Purchase Modal */}
            <TokenPurchaseModal
                isOpen={showTokenModal}
                onClose={() => setShowTokenModal(false)}
                onSuccess={handleTokenPurchaseSuccess}
            />

            {/* Mock Interview Modal */}
            <MockInterviewModal
                isOpen={showMockInterviewModal}
                showConsentOnly={mockInterviewPaid && !mockInterviewConsented}
                onClose={() => {
                    setShowMockInterviewModal(false);
                    navigate("/");
                }}
                onStartPracticing={async () => {
                    setShowMockInterviewModal(false);
                    // Refresh payment and consent status
                    try {
                        const { data } = await supabase
                            .from('user_tokens')
                            .select('mock_interview_paid, mock_interview_consent')
                            .eq('user_id', user.id)
                            .single();
                        
                        if (data) {
                            setMockInterviewPaid(data.mock_interview_paid || false);
                            setMockInterviewConsented(data.mock_interview_consent || false);
                        }
                    } catch (err) {
                        console.error("Error refreshing payment status:", err);
                    }
                    navigate("/practice-interview-questions");
                }}
            />
        </nav>
    );
};

export default Sidebar;
